'use client'

import * as React from 'react'
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle, RotateCcw } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Toast Notification System
 * 
 * Spanish-first toast notifications implementing BIRHAUS principles:
 * - Max 3 toasts at once (cognitive load reduction)
 * - Spanish-first messages ("Guardado exitosamente")
 * - Undo support for destructive actions
 * - Auto-dismiss with countdown
 * - Accessibility announcements
 * - Performance optimized animations
 * - WCAG AA+ compliance
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface BirhausToastData {
  id: string
  variant: ToastVariant
  title?: string
  titleEs?: string
  titleEn?: string
  message?: string
  messageEs?: string
  messageEn?: string
  duration?: number
  persistent?: boolean
  undoAction?: () => void | Promise<void>
  undoLabel?: string
  undoLabelEs?: string
  undoLabelEn?: string
  action?: {
    label: string
    labelEs?: string
    labelEn?: string
    onClick: () => void
  }
  onDismiss?: (id: string) => void
  locale?: 'es' | 'en'
}

interface ToastContextType {
  toasts: BirhausToastData[]
  addToast: (toast: Omit<BirhausToastData, 'id'>) => string
  dismissToast: (id: string) => void
  clearAllToasts: () => void
  locale: 'es' | 'en'
  setLocale: (locale: 'es' | 'en') => void
}

const ToastContext = React.createContext<ToastContextType | null>(null)

// Toast Provider Component
export interface BirhausToastProviderProps {
  children: React.ReactNode
  maxToasts?: number
  defaultLocale?: 'es' | 'en'
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

export const BirhausToastProvider = ({
  children,
  maxToasts = 3, // BIRHAUS: Max 3 for cognitive load
  defaultLocale = 'es',
  position = 'top-right'
}: BirhausToastProviderProps) => {
  const [toasts, setToasts] = React.useState<BirhausToastData[]>([])
  const [locale, setLocale] = React.useState<'es' | 'en'>(defaultLocale)
  
  const addToast = React.useCallback((toastData: Omit<BirhausToastData, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast: BirhausToastData = {
      ...toastData,
      id,
      locale: toastData.locale || locale
    }
    
    setToasts(current => {
      let updated = [...current, newToast]
      
      // BIRHAUS: Enforce max toasts (cognitive load)
      if (updated.length > maxToasts) {
        // Remove oldest non-persistent toasts first
        const persistentToasts = updated.filter(t => t.persistent)
        const nonPersistentToasts = updated.filter(t => !t.persistent)
        
        if (nonPersistentToasts.length > maxToasts - persistentToasts.length) {
          const keepCount = maxToasts - persistentToasts.length
          updated = [
            ...persistentToasts,
            ...nonPersistentToasts.slice(-keepCount)
          ]
        }
      }
      
      return updated
    })
    
    return id
  }, [locale, maxToasts])
  
  const dismissToast = React.useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id))
  }, [])
  
  const clearAllToasts = React.useCallback(() => {
    setToasts([])
  }, [])
  
  const contextValue: ToastContextType = {
    toasts,
    addToast,
    dismissToast,
    clearAllToasts,
    locale,
    setLocale
  }
  
  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  }
  
  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Toast Container */}
      <div 
        className={cn(
          'fixed z-50 pointer-events-none',
          safeAccess(positionClasses, position, positionClasses['top-right'])
        )}
        aria-live="polite"
        aria-label="Notificaciones"
      >
        <div className="flex flex-col space-y-2 w-80 max-w-sm">
          {toasts.map((toast) => (
            <BirhausToast
              key={toast.id}
              toast={toast}
              onDismiss={dismissToast}
            />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  )
}

// Individual Toast Component
interface BirhausToastProps {
  toast: BirhausToastData
  onDismiss: (id: string) => void
}

const BirhausToast = ({ toast, onDismiss }: BirhausToastProps) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const [timeRemaining, setTimeRemaining] = React.useState(toast.duration || 5000)
  const [isPaused, setIsPaused] = React.useState(false)
  const timeoutRef = React.useRef<NodeJS.Timeout>()
  const intervalRef = React.useRef<NodeJS.Timeout>()
  
  // Spanish-first text resolution
  const getDisplayText = React.useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    const locale = toast.locale || 'es'
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [toast.locale])
  
  const displayTitle = getDisplayText(toast.title, toast.titleEs, toast.titleEn, '')
  const displayMessage = getDisplayText(toast.message, toast.messageEs, toast.messageEn, '')
  const displayUndoLabel = getDisplayText(toast.undoLabel, toast.undoLabelEs, toast.undoLabelEn, 'Deshacer')
  const displayActionLabel = toast.action ? getDisplayText(
    toast.action.label,
    toast.action.labelEs,
    toast.action.labelEn,
    toast.action.label
  ) : ''
  
  // Handle dismiss
  const handleDismiss = React.useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      onDismiss(toast.id)
      toast.onDismiss?.(toast.id)
    }, 200) // Wait for exit animation
  }, [onDismiss, toast.id, toast.onDismiss])
  
  // Handle undo action
  const handleUndo = React.useCallback(async () => {
    if (toast.undoAction) {
      try {
        await toast.undoAction()
        handleDismiss()
      } catch (error) {
        console.error('Error executing undo action:', error)
      }
    }
  }, [toast.undoAction, handleDismiss])
  
  // Auto-dismiss timer
  React.useEffect(() => {
    if (toast.persistent) return
    
    setIsVisible(true)
    
    const startTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      
      timeoutRef.current = setTimeout(() => {
        handleDismiss()
      }, timeRemaining)
    }
    
    const startCountdown = () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 100
          if (newTime <= 0) {
            clearInterval(intervalRef.current!)
            return 0
          }
          return newTime
        })
      }, 100)
    }
    
    if (!isPaused) {
      startTimer()
      startCountdown()
    }
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [toast.persistent, timeRemaining, isPaused, handleDismiss])
  
  // Show animation on mount
  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50)
    return () => clearTimeout(timer)
  }, [])
  
  // Pause/resume on hover
  const handleMouseEnter = React.useCallback(() => {
    if (!toast.persistent) {
      setIsPaused(true)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [toast.persistent])
  
  const handleMouseLeave = React.useCallback(() => {
    if (!toast.persistent) {
      setIsPaused(false)
    }
  }, [toast.persistent])
  
  // Variant styles
  const variantStyles = {
    success: {
      bg: 'bg-green-50 border-green-200',
      icon: CheckCircle2,
      iconColor: 'text-green-500',
      titleColor: 'text-green-800',
      messageColor: 'text-green-700',
      progressColor: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50 border-red-200',
      icon: AlertCircle,
      iconColor: 'text-red-500',
      titleColor: 'text-red-800',
      messageColor: 'text-red-700',
      progressColor: 'bg-red-500'
    },
    warning: {
      bg: 'bg-yellow-50 border-yellow-200',
      icon: AlertTriangle,
      iconColor: 'text-yellow-500',
      titleColor: 'text-yellow-800',
      messageColor: 'text-yellow-700',
      progressColor: 'bg-yellow-500'
    },
    info: {
      bg: 'bg-blue-50 border-blue-200',
      icon: Info,
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      messageColor: 'text-blue-700',
      progressColor: 'bg-blue-500'
    }
  }
  
  const variant = variantStyles[toast.variant]
  const IconComponent = variant.icon
  
  // Progress percentage
  const progressPercentage = toast.persistent ? 100 : 
    ((toast.duration || 5000) - timeRemaining) / (toast.duration || 5000) * 100
  
  return (
    <div
      className={cn(
        'pointer-events-auto transform transition-all duration-200 ease-out',
        'border rounded-lg shadow-lg max-w-sm w-full',
        variant.bg,
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="polite"
    >
      {/* Progress Bar */}
      {!toast.persistent && (
        <div className="h-1 bg-gray-200 rounded-t-lg overflow-hidden">
          <div 
            className={cn('h-full transition-all duration-100 ease-linear', variant.progressColor)}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start space-x-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <IconComponent className={cn('h-5 w-5', variant.iconColor)} />
          </div>
          
          {/* Text Content */}
          <div className="flex-1 min-w-0">
            {displayTitle && (
              <h4 className={cn('text-sm font-medium', variant.titleColor)}>
                {displayTitle}
              </h4>
            )}
            {displayMessage && (
              <p className={cn(
                'text-sm mt-1',
                variant.messageColor,
                !displayTitle && 'font-medium'
              )}>
                {displayMessage}
              </p>
            )}
            
            {/* Actions */}
            {(toast.undoAction || toast.action) && (
              <div className="flex items-center space-x-2 mt-3">
                {toast.undoAction && (
                  <button
                    type="button"
                    onClick={handleUndo}
                    className="inline-flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    <span>{displayUndoLabel}</span>
                  </button>
                )}
                
                {toast.action && (
                  <button
                    type="button"
                    onClick={toast.action.onClick}
                    className={cn(
                      'text-sm font-medium transition-colors',
                      variant.titleColor,
                      `hover:${variant.titleColor.replace('800', '900')}`
                    )}
                  >
                    {displayActionLabel}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Close Button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar notificación"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// Hook to use toast functionality
export const useBirhausToast = () => {
  const context = React.useContext(ToastContext)
  
  if (!context) {
    throw new Error('useBirhausToast must be used within a BirhausToastProvider')
  }
  
  const { addToast, dismissToast, clearAllToasts, locale } = context
  
  // Convenience methods with Spanish-first defaults
  const showSuccess = React.useCallback((
    message: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    return addToast({
      variant: 'success',
      messageEs: message,
      titleEs: 'Éxito',
      titleEn: 'Success',
      duration: 4000,
      ...options
    })
  }, [addToast])
  
  const showError = React.useCallback((
    message: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    return addToast({
      variant: 'error',
      messageEs: message,
      titleEs: 'Error',
      titleEn: 'Error',
      duration: 6000,
      persistent: true, // Errors should be persistent
      ...options
    })
  }, [addToast])
  
  const showWarning = React.useCallback((
    message: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    return addToast({
      variant: 'warning',
      messageEs: message,
      titleEs: 'Advertencia',
      titleEn: 'Warning',
      duration: 5000,
      ...options
    })
  }, [addToast])
  
  const showInfo = React.useCallback((
    message: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    return addToast({
      variant: 'info',
      messageEs: message,
      titleEs: 'Información',
      titleEn: 'Information',
      duration: 4000,
      ...options
    })
  }, [addToast])
  
  // IPU Admin specific convenience methods
  const showSaved = React.useCallback((
    entity?: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    const message = entity ? `${entity} guardado exitosamente` : 'Guardado exitosamente'
    return showSuccess(message, options)
  }, [showSuccess])
  
  const showDeleted = React.useCallback((
    entity?: string,
    undoAction?: () => void,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    const message = entity ? `${entity} eliminado` : 'Elemento eliminado'
    return addToast({
      variant: 'success',
      messageEs: message,
      titleEs: 'Eliminado',
      titleEn: 'Deleted',
      duration: 8000, // Longer for undo actions
      undoAction,
      undoLabelEs: 'Deshacer',
      undoLabelEn: 'Undo',
      ...options
    })
  }, [addToast])
  
  const showPaymentSuccess = React.useCallback((
    amount: string,
    options?: Partial<Omit<BirhausToastData, 'id' | 'variant' | 'message'>>
  ) => {
    return showSuccess(`Pago de ${amount} procesado exitosamente`, {
      titleEs: 'Pago Exitoso',
      titleEn: 'Payment Successful',
      ...options
    })
  }, [showSuccess])
  
  return {
    // Core methods
    addToast,
    dismissToast,
    clearAllToasts,
    
    // Convenience methods
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // IPU Admin specific
    showSaved,
    showDeleted,
    showPaymentSuccess,
    
    // Context
    locale,
    toasts: context.toasts
  }
}

// Standalone Toast component for manual usage
export interface BirhausToastStandaloneProps extends Omit<BirhausToastData, 'id'> {
  visible?: boolean
  onClose?: () => void
}

export const BirhausToastStandalone = React.forwardRef<HTMLDivElement, BirhausToastStandaloneProps>(({
  visible = true,
  onClose,
  ...toastData
}, ref) => {
  const [internalVisible, setInternalVisible] = React.useState(visible)
  
  React.useEffect(() => {
    setInternalVisible(visible)
  }, [visible])
  
  const handleDismiss = React.useCallback(() => {
    setInternalVisible(false)
    setTimeout(() => {
      onClose?.()
    }, 200)
  }, [onClose])
  
  if (!internalVisible) return null
  
  const mockToast: BirhausToastData = {
    id: 'standalone',
    ...toastData
  }
  
  return (
    <div ref={ref} className="w-80 max-w-sm">
      <BirhausToast
        toast={mockToast}
        onDismiss={handleDismiss}
      />
    </div>
  )
})

BirhausToastStandalone.displayName = 'BirhausToastStandalone'

// Common toast messages for IPU Admin
export const birhausToastMessages = {
  // General CRUD operations
  saved: {
    es: 'Guardado exitosamente',
    en: 'Saved successfully'
  },
  deleted: {
    es: 'Eliminado exitosamente',
    en: 'Deleted successfully'
  },
  updated: {
    es: 'Actualizado exitosamente',
    en: 'Updated successfully'
  },
  created: {
    es: 'Creado exitosamente',
    en: 'Created successfully'
  },
  
  // Church/Member specific
  memberAdded: {
    es: 'Miembro agregado exitosamente',
    en: 'Member added successfully'
  },
  memberUpdated: {
    es: 'Información del miembro actualizada',
    en: 'Member information updated'
  },
  memberDeleted: {
    es: 'Miembro eliminado',
    en: 'Member deleted'
  },
  
  // Financial operations
  donationRecorded: {
    es: 'Donación registrada exitosamente',
    en: 'Donation recorded successfully'
  },
  paymentProcessed: {
    es: 'Pago procesado exitosamente',
    en: 'Payment processed successfully'
  },
  receiptGenerated: {
    es: 'Recibo generado exitosamente',
    en: 'Receipt generated successfully'
  },
  
  // Errors
  saveError: {
    es: 'Error al guardar. Intenta nuevamente.',
    en: 'Error saving. Please try again.'
  },
  deleteError: {
    es: 'Error al eliminar. Intenta nuevamente.',
    en: 'Error deleting. Please try again.'
  },
  networkError: {
    es: 'Error de conexión. Verifica tu internet.',
    en: 'Connection error. Check your internet.'
  },
  validationError: {
    es: 'Por favor corrige los errores en el formulario',
    en: 'Please fix the errors in the form'
  }
}